/**
 * LoginForm — React Hook Form + Zod validation.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, TextField } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

interface LoginFormValues {
  username: string;
  password: string;
}

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => Promise<void>;
}

export default function LoginForm({ onSubmit }: LoginFormProps) {
  const { t } = useTranslation("auth");
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const schema = useMemo(() => z.object({
    username: z.string().trim().min(1, t("usernameRequired")),
    password: z.string().min(1, t("passwordRequired")),
  }), [t]);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: "", password: "" },
  });

  const doSubmit = async (data: LoginFormValues) => {
    setServerError(null);
    try {
      await onSubmit(data);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : t("loginFailed"));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(doSubmit)} noValidate sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {serverError && <Alert severity="error" onClose={() => setServerError(null)}>{serverError}</Alert>}
      <TextField
        label={t("usernameLabel")}
        autoComplete="username"
        autoCapitalize="none"
        autoFocus
        fullWidth
        error={!!errors.username}
        helperText={errors.username?.message}
        {...register("username")}
      />
      <TextField
        label={t("passwordLabel")}
        type={showPassword ? "text" : "password"}
        autoComplete="current-password"
        fullWidth
        error={!!errors.password}
        helperText={errors.password?.message}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  size="small"
                  aria-label={showPassword ? t("hidePassword") : t("showPassword")}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        {...register("password")}
      />
      <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting} sx={{ mt: 1, py: 1.25 }}>
        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t("loginButton")}
      </Button>
    </Box>
  );
}
